import { useQuery } from '@tanstack/react-query';
import { fetchOpsDashboard } from '@/lib/api';

export type MessageDirection = 'in' | 'out';

export type MessageStatus =
  | 'received'
  | 'validated'
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'acknowledged'
  | 'failed'
  | 'rejected';

export type ThreadStatus = 'open' | 'closed' | 'error';

export type AckType = 'AA' | 'AE' | 'AR';

export type AckStatus = 'received' | 'processed' | 'failed';

export type AckMatchStatus = 'matched' | 'unmatched';

export type AckSummary = {
  id: string;
  ackType: AckType;
  status: AckStatus;
  bundleId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  matchStatus: AckMatchStatus | null;
};

export type MessageSummary = {
  id: string;
  threadId: string | null;
  direction: MessageDirection;
  status: MessageStatus;
  communicationIdentifier: string;
  bundleId: string | null;
  senderSor: string | null;
  receiverSor: string | null;
  sentAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  latestAcknowledgement: AckSummary | null;
  acknowledgementCount: number;
};

export type ThreadMeta = {
  id: string;
  communicationIdentifier: string;
  subject: string | null;
  status: ThreadStatus;
  createdAt: string;
  updatedAt: string;
};

export type OpsListOutput<T> = {
  items: T[];
  pagination: { limit: number; offset: number; total: number };
};

export const MESSAGE_STATUSES: Array<MessageStatus | 'all'> = [
  'all',
  'received',
  'validated',
  'queued',
  'sent',
  'delivered',
  'acknowledged',
  'failed',
  'rejected',
];

export const MESSAGE_DIRECTIONS: Array<MessageDirection | 'all'> = ['all', 'in', 'out'];

export const THREAD_STATUSES: Array<ThreadStatus | 'all'> = ['all', 'open', 'closed', 'error'];

export const OPS_PAGE_SIZE = 50;

export type OpsMessageFilters = {
  status: MessageStatus | 'all';
  direction: MessageDirection | 'all';
  offset: number;
};

export type OpsThreadFilters = {
  status: ThreadStatus | 'all';
  offset: number;
};

export const opsMessagesQueryKey = (filters: OpsMessageFilters) =>
  ['ops-dashboard', 'messages', filters] as const;

export const opsThreadsQueryKey = (filters: OpsThreadFilters) =>
  ['ops-dashboard', 'threads', filters] as const;

export const opsAcknowledgementsQueryKey = (offset: number) =>
  ['ops-dashboard', 'acknowledgements', offset] as const;

export function useOpsMessages(filters: OpsMessageFilters) {
  return useQuery({
    queryKey: opsMessagesQueryKey(filters),
    queryFn: () =>
      fetchOpsDashboard<OpsListOutput<MessageSummary>>('messages', {
        status: filters.status === 'all' ? undefined : filters.status,
        direction: filters.direction === 'all' ? undefined : filters.direction,
        limit: String(OPS_PAGE_SIZE),
        offset: String(filters.offset),
      }),
  });
}

export function useOpsThreads(filters: OpsThreadFilters) {
  return useQuery({
    queryKey: opsThreadsQueryKey(filters),
    queryFn: () =>
      fetchOpsDashboard<OpsListOutput<ThreadMeta>>('threads', {
        status: filters.status === 'all' ? undefined : filters.status,
        limit: String(OPS_PAGE_SIZE),
        offset: String(filters.offset),
      }),
  });
}

export type MedbinPreviewSummary = {
  id: string;
  consumerName: string;
  kind: 'XBIN01' | 'XBIN02';
  fileName: string;
  senderEan: string;
  receiverEan: string;
  validationOk: boolean;
  createdAt: string;
};

export function useOpsMedbinPreviews(offset: number) {
  return useQuery({
    queryKey: ['ops-dashboard', 'medbin-previews', offset] as const,
    queryFn: () =>
      fetchOpsDashboard<OpsListOutput<MedbinPreviewSummary>>('medbin-previews', {
        limit: String(OPS_PAGE_SIZE),
        offset: String(offset),
      }),
  });
}

export function useOpsAcknowledgements(offset: number) {
  return useQuery({
    queryKey: opsAcknowledgementsQueryKey(offset),
    queryFn: () =>
      fetchOpsDashboard<OpsListOutput<AckSummary>>('acknowledgements', {
        limit: String(OPS_PAGE_SIZE),
        offset: String(offset),
      }),
  });
}
